import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, of } from 'rxjs';

// Known business types — extend this list as new domains are added
export type BusinessType = 'RETAIL' | 'MOBILE_SHOP' | string;

export enum BusinessDomain {
  RETAIL = 'RETAIL',
  MOBILE_SHOP = 'MOBILE_SHOP'
}

export enum IndustryType {
  RETAIL = 6,
  MOBILE_SHOP = 14
}

/** Maps industryType number → business type segment string used in API URLs */
export const BUSINESS_TYPE_URL_SEGMENT: Record<string, string> = {
  [BusinessDomain.RETAIL]: 'retail',
  [BusinessDomain.MOBILE_SHOP]: 'mobile',
  // Future: RESTAURANT: 'restaurant', PHARMACY: 'pharmacy', etc.
};

export interface ClientDetails {
  industryType?: number;
  industrytype?: number;
  industry?: number;
  industry_id?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private http = inject(HttpClient);

  // Core state signal
  private readonly clientDetailSignal = signal<ClientDetails | null>(null);

  constructor() {
    this.loadCachedConfig();
  }

  // Expose read-only signals
  readonly clientDetail = computed(() => this.clientDetailSignal());

  readonly industryType = computed(() => {
    const details = this.clientDetail();
    console.log('[TenantService] clientDetail value:', details);
    if (!details) return null;
    const value = details.industryType ?? details.industrytype ?? details.industry ?? details.industry_id ?? null;
    console.log('[TenantService] computed industryType:', value);
    return value;
  });

  readonly businessType = computed<BusinessType>(() => {
    // 14 is Mobile Shop, 6 is Retail. Defaulting to Retail if unknown to avoid crashes.
    const type = this.industryType() === IndustryType.MOBILE_SHOP ? BusinessDomain.MOBILE_SHOP : BusinessDomain.RETAIL;
    console.log('[TenantService] computed businessType:', type);
    return type;
  });

  readonly themeClass = computed<string>(() => {
    return this.businessType() === BusinessDomain.MOBILE_SHOP ? 'theme-mobile' : 'theme-retail';
  });

  /**
   * Fetches client details from the backend.
   * This should be called right after AuthService validates authentication.
   */
  checkClientDetail(): Observable<ClientDetails> {
    // Replace with the actual endpoint URL for client details
    return this.http.get<ClientDetails>('/api/clientDetail').pipe(
      tap((details) => {
        this.clientDetailSignal.set(details);
        localStorage.setItem('elixr_client_details', JSON.stringify(details));
      })
    );
  }

  loadCachedConfig(): boolean {
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser);

        const industryVal = user.industry ?? user.industryType ?? user.industry_id;
        if (user && industryVal !== undefined && industryVal !== null) {
          this.clientDetailSignal.set({ industryType: Number(industryVal) });
          return true;
        }

        // Handle Superadmin / Admin edge cases where industry might be null or role is 0
        if (user && (user.role === 0 || user.isAdmin === 0 || user.industry === null || user.industry === undefined)) {
          // Set a default industry type (6 = RETAIL) so the guard allows them inside
          this.clientDetailSignal.set({ industryType: IndustryType.RETAIL });
          return true;
        }
      } catch {
        // Fall through
      }
    }

    // Fallback to legacy client details
    const cached = localStorage.getItem('elixr_client_details');
    if (cached) {
      try {
        this.clientDetailSignal.set(JSON.parse(cached));
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }

  clearConfig(): void {
    this.clientDetailSignal.set(null);
    localStorage.removeItem('elixr_client_details');
  }
}
